"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, ButtonBase, Typography, CircularProgress } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/icons";
import { useDarkColors } from "@/hooks/useDarkColors";
import { useTranslator } from "@/lang";
import { useAuthStore } from "@/stores/authStore";
import { useLoginModalStore } from "@/stores/loginModalStore";
import { getCollection, setAssetFavorite } from "@/api/feedApi";
import { getMuseAssetList, type MuseAssetItem } from "@/api/museAssetApi";
import { getAssetUrl } from "@/utils/assetUrl";
import { getLocalizedText } from "@/utils/getLocalizedText";
import {
  registerTopPriorityBackInterceptor,
  unregisterTopPriorityBackInterceptor,
} from "@/stores/navigationStackStore";
import type {
  CollectionAssetDto,
  GetCollectionOutputDto,
} from "@/shared/types/api/Api";
import { GACHA_GRADE_CONFIG, type GachaGrade } from "@/constants/tempColors";
import {
  TEMP_TEXT_BOLD_L,
  TEMP_TEXT_BOLD_M,
  TEMP_TEXT_BOLD_XS,
  TEMP_TEXT_SEMIBOLD_M,
  TEMP_TEXT_SEMIBOLD_XS,
} from "@/constants/tempTypo";

// ============================================================
// 나의 앨범 — 여태 모은(구매·해금·뽑기) 전체 에셋을 한 곳에서 감상
// (캐릭터 프로필별로 흩어진 앨범을 통합한 뷰. 나의 세계 하단 버튼으로 진입)
// ============================================================

const GROUP_GRADIENT = "linear-gradient(135deg, #7c3aed 0%, #FE375B 100%)";
const HEART_COLOR = "#FE375B";
/** 에피소드 칩/배지 색 — 에피소드 시그니처 보라(프로필 그리드 배지와 동일 계열) */
const EPISODE_CHIP_COLOR = "#7c3aed";
/** 즐겨찾기 별 색상 (골드) */
const FAVORITE_COLOR = "#FFC83D";

/** 그리드 무한스크롤 한 묶음 크기 (3열 × 6행). 모바일 렉 방지용 점진 렌더 */
const GRID_PAGE = 18;

/** 등급 우선순위: L → U → S → R → N */
const GRADE_ORDER: Record<string, number> = { L: 0, U: 1, S: 2, R: 3, N: 4 };
const GRADE_SEQUENCE: GachaGrade[] = ["L", "U", "S", "R", "N"];

/** 가챠 등급 카드 프레임 그라디언트 (MyCollectionTab과 동일 체계) */
const GRADE_FRAME: Record<string, string> = {
  L: "linear-gradient(180deg, #FFE76F 0%, #8000FF 100%)",
  U: "linear-gradient(180deg, #DDB139 0%, #462300 100%)",
  S: "linear-gradient(180deg, #DADADA 0%, #5A5A5A 100%)",
  R: "linear-gradient(180deg, #4777FF 0%, #0F46E4 100%)",
  N: "linear-gradient(180deg, #834D10 0%, #5F390F 100%)",
};

type FilterKey = "soft" | "hard";

/** 친밀도 조건(LEVEL0~LEVEL10) → 레벨 숫자 */
function getIntimacyLevel(condition: string): number {
  const m = condition.match(/LEVEL(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

/** 비디오 에셋의 포스터(썸네일) 경로 — MyCollectionTab과 동일 규칙 */
function getVideoPosterPath(assetPath: string): string {
  const lastDot = assetPath.lastIndexOf(".");
  const basePath = lastDot > 0 ? assetPath.substring(0, lastDot) : assetPath;
  return `${basePath}_poster.webp`;
}

/** 본인 언어 → ko 폴백 음성 URL */
function pickVoiceUrl(voicePath: unknown, language: string): string | null {
  if (!voicePath || typeof voicePath !== "object") return null;
  const vp = voicePath as Record<string, string>;
  const url = vp[language] || vp["ko"] || "";
  return url ? getAssetUrl(url) : null;
}

/** 수위 → 하드 여부 (소프트 필터 = SOFT+MEDIUM, 하드 필터 = HARD) */
function isHardLevel(adultLevel?: string): boolean {
  return adultLevel === "HARD";
}

/** 친밀도 단계 분포 버킷 — label은 i18n 키, 렌더 시점에 t() 적용 */
const INTIMACY_BUCKETS: { label: string; test: (lv: number) => boolean }[] = [
  { label: "galaxy.album.bucket.none", test: (lv) => lv === 0 },
  { label: "galaxy.album.bucket.low", test: (lv) => lv >= 1 && lv <= 4 },
  { label: "galaxy.album.bucket.mid", test: (lv) => lv >= 5 && lv <= 7 },
  { label: "galaxy.album.bucket.high", test: (lv) => lv >= 8 },
];

/** intimacyLevel → 버킷 인덱스 (친밀도 필터용). 매칭 없으면 -1 */
function getIntimacyBucketIndex(level: number): number {
  return INTIMACY_BUCKETS.findIndex((b) => b.test(level));
}

/** 그리드/뷰어가 공유하는 평탄화된 에셋 */
interface CollectedAsset {
  id: string;
  assetPath: string;
  assetType: string;
  grade: GachaGrade;
  intimacyLevel: number;
  adultLevel?: string;
  voicePath: unknown;
  isGacha: boolean;
  /** 에피소드 컷(가상 id "episode:...") — 등급 대신 에피소드 칩, 에피소드 필터 대상 */
  isEpisode: boolean;
  isFavorite: boolean;
  characterId: string;
  characterName: string;
  profileImagePath: string | null;
}

function toCollected(
  a: CollectionAssetDto,
  characterName: string,
  profileImagePath: string | null,
  characterId: string,
): CollectedAsset {
  return {
    id: a.id,
    assetPath: a.assetPath,
    assetType: a.assetType,
    grade: (a.grade as GachaGrade) ?? "N",
    intimacyLevel: getIntimacyLevel(a.intimacyCondition),
    adultLevel: a.adultLevel,
    voicePath: a.voicePath ?? null,
    isGacha: !!a.itemId || a.pieceCost > 0,
    isEpisode: a.id.startsWith("episode:"),
    isFavorite: !!a.isFavorite,
    characterId,
    characterName,
    profileImagePath,
  };
}

export function AssetCollection({ onBackPress }: { onBackPress: () => void }) {
  const c = useDarkColors();
  const { t, language } = useTranslator();
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const openLoginModal = useLoginModalStore((s) => s.open);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    // MyCollectionTab / CollectionDetail 과 동일 키 → 캐시 공유
    queryKey: ["collection-detail"],
    queryFn: getCollection,
    enabled: isLoggedIn,
    staleTime: 60_000,
  });

  const [filter, setFilter] = useState<Set<FilterKey>>(
    () => new Set<FilterKey>(["soft", "hard"]),
  );
  const [favOnly, setFavOnly] = useState(false);
  // 에피소드 컷만 보기 — 친밀도 칩 줄의 "에피소드" 칩 토글
  const [episodeOnly, setEpisodeOnly] = useState(false);
  // 직접생성(커스텀)만 보기 — 수위 칩 줄의 "커스텀" 칩 토글 (즐겨찾기처럼 별개 모드)
  const [usergenOnly, setUsergenOnly] = useState(false);
  // 등급/친밀도 필터 — 멀티선택. 빈 Set = 전체(필터 없음)
  const [gradeSel, setGradeSel] = useState<Set<string>>(() => new Set());
  const [intimacySel, setIntimacySel] = useState<Set<number>>(() => new Set());
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // 직접생성(커스텀) 에셋 — 상단 스트립·"커스텀" 칩 카운트 공용 (스트립과 같은 쿼리키 = 캐시 공유)
  const { data: usergenData } = useQuery({
    queryKey: ["muse-asset", "list", "all"],
    queryFn: () => getMuseAssetList(),
    enabled: isLoggedIn,
    staleTime: 30_000,
  });
  const usergenCount = useMemo(
    () => (usergenData?.items ?? []).filter((it) => it.status === "COMPLETED" && it.assetPath).length,
    [usergenData],
  );

  // 즐겨찾기 토글 — 낙관적으로 캐시(["collection-detail"])를 갱신하고 실패 시 롤백.
  // collected/visible 이 data에서 파생되므로 그리드·필터·뷰어가 즉시 같이 반영됨.
  const favoriteMutation = useMutation({ mutationFn: ({ assetId, next }: { assetId: string; next: boolean }) => setAssetFavorite(assetId, next) });

  const patchFavoriteInCache = useCallback(
    (assetId: string, next: boolean) => {
      queryClient.setQueryData(
        ["collection-detail"],
        (old: GetCollectionOutputDto | undefined) => {
          if (!old) return old;
          return {
            ...old,
            characters: old.characters.map((ch) => ({
              ...ch,
              assets: ch.assets.map((a) =>
                a.id === assetId ? { ...a, isFavorite: next } : a,
              ),
            })),
          };
        },
      );
    },
    [queryClient],
  );

  const toggleFavorite = useCallback(
    (assetId: string, next: boolean) => {
      patchFavoriteInCache(assetId, next); // optimistic
      favoriteMutation.mutate(
        { assetId, next },
        { onError: () => patchFavoriteInCache(assetId, !next) }, // rollback
      );
    },
    [favoriteMutation, patchFavoriteInCache],
  );

  // 수집(소유 or 해금)한 에셋만 평탄화. 등급 높은 순 → 캐릭터 순.
  const collected = useMemo(() => {
    const out: CollectedAsset[] = [];
    for (const ch of data?.characters ?? []) {
      const name = getLocalizedText(ch.characterName, language);
      for (const a of ch.assets) {
        if (!(a.isOwned || a.isUnlocked)) continue;
        if (!a.assetPath) continue;
        out.push(
          toCollected(a, name, ch.profileImagePath ?? null, ch.characterId),
        );
      }
    }
    out.sort(
      (a, b) =>
        (GRADE_ORDER[a.grade] ?? 99) - (GRADE_ORDER[b.grade] ?? 99) ||
        b.intimacyLevel - a.intimacyLevel ||
        a.characterId.localeCompare(b.characterId),
    );
    return out;
  }, [data, language]);

  const totalAssets = data?.totalAssets ?? 0;
  const totalCollected = collected.length;
  const collectedPercent =
    totalAssets > 0 ? Math.round((totalCollected / totalAssets) * 100) : 0;

  // 등급 분포 (뽑기 아이템 한정 — 실제 등급이 의미 있는 것)
  const gradeStats = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of collected)
      if (a.isGacha) counts[a.grade] = (counts[a.grade] ?? 0) + 1;
    const total = Object.values(counts).reduce((s, n) => s + n, 0);
    const rows = GRADE_SEQUENCE.filter((g) => (counts[g] ?? 0) > 0).map(
      (g) => ({
        grade: g,
        count: counts[g] ?? 0,
        color: GACHA_GRADE_CONFIG[g].borderColor,
      }),
    );
    return { rows, total };
  }, [collected]);

  // 친밀도 분포
  const intimacyStats = useMemo(() => {
    return INTIMACY_BUCKETS.map((b, idx) => ({
      idx,
      label: b.label,
      count: collected.filter((a) => b.test(a.intimacyLevel)).length,
    })).filter((b) => b.count > 0);
  }, [collected]);

  // 수위별 카운트 (필터 칩 라벨용)
  const softCount = useMemo(
    () => collected.filter((a) => !isHardLevel(a.adultLevel)).length,
    [collected],
  );
  const hardCount = useMemo(
    () => collected.filter((a) => isHardLevel(a.adultLevel)).length,
    [collected],
  );
  const favCount = useMemo(
    () => collected.filter((a) => a.isFavorite).length,
    [collected],
  );
  const episodeCount = useMemo(
    () => collected.filter((a) => a.isEpisode).length,
    [collected],
  );

  const visible = useMemo(
    () =>
      collected.filter((a) => {
        // 즐겨찾기는 별개 모드 — 켜면 다른 필터와 무관하게 즐겨찾기 전체를 보여준다
        if (favOnly) return a.isFavorite;
        // 에피소드만 보기
        if (episodeOnly && !a.isEpisode) return false;
        // 수위 (소프트/하드)
        if (!(isHardLevel(a.adultLevel) ? filter.has("hard") : filter.has("soft")))
          return false;
        // 등급 (선택 없으면 전체). 등급은 뽑기 에셋 개념이므로 isGacha에만 적용
        if (gradeSel.size > 0 && !(a.isGacha && gradeSel.has(a.grade)))
          return false;
        // 친밀도 버킷 (선택 없으면 전체)
        if (
          intimacySel.size > 0 &&
          !intimacySel.has(getIntimacyBucketIndex(a.intimacyLevel))
        )
          return false;
        return true;
      }),
    [collected, filter, favOnly, episodeOnly, gradeSel, intimacySel],
  );

  // 즐겨찾기가 아닌 다른 필터를 건드리면 즐겨찾기 모드를 자동 해제한다
  // (즐겨찾기를 다시 눌러 끌 필요 없이 바로 그 필터가 적용되도록)
  const toggleChip = useCallback((key: FilterKey) => {
    setFavOnly(false);
    setUsergenOnly(false);
    setFilter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleGrade = useCallback((g: string) => {
    setFavOnly(false);
    setUsergenOnly(false);
    setGradeSel((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  }, []);

  const toggleIntimacy = useCallback((idx: number) => {
    setFavOnly(false);
    setUsergenOnly(false);
    setIntimacySel((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  // 무한스크롤 — 한 번에 다 그리면 모바일에서 렉이 심하므로 GRID_PAGE 만큼 끊어 렌더하고
  // 바닥 근처(rootMargin)에 닿으면 다음 묶음을 추가한다. (데이터는 통계용으로 한 번에 받지만
  // DOM/이미지는 점진적으로만 올린다)
  const [shownCount, setShownCount] = useState(GRID_PAGE);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // 필터가 바뀌면 처음부터 다시 보여주고 맨 위로 스크롤
  useEffect(() => {
    setShownCount(GRID_PAGE);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [favOnly, usergenOnly, filter, gradeSel, intimacySel]);

  // 바닥 감지 → 다음 묶음 로드
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShownCount((n) => (n < visible.length ? n + GRID_PAGE : n));
        }
      },
      { root: scrollContainerRef.current, rootMargin: "600px 0px" },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [visible.length]);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: c.bg,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: "4px",
          px: "12px",
          pt: "calc(env(safe-area-inset-top, 0px) + 14px)",
          pb: "12px",
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <ButtonBase
          onClick={onBackPress}
          aria-label={t("galaxy.aria.backToWorld")}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ml: "-6px",
          }}
        >
          <Icon name="icChevronLeft" size={26} color={c.fg} />
        </ButtonBase>
        <Typography sx={{ ...TEMP_TEXT_BOLD_L, color: c.fg }}>
          {t("galaxy.album.title")}
        </Typography>
      </Box>

      {/* 본문 */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {!isLoggedIn ? (
          <EmptyState
            icon="icLock"
            title={t("galaxy.album.loginTitle")}
            subtitle={t("galaxy.album.loginSubtitle")}
            cta={{ label: t("galaxy.album.loginCta"), onClick: openLoginModal }}
          />
        ) : isLoading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={34} sx={{ color: c.primary }} />
          </Box>
        ) : (
          <Box
            sx={{
              px: "16px",
              pt: "16px",
              pb: "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 24px)",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {/* 직접생성 스냅샷·배경 — 내 눈에만 보이는 커스텀 에셋 (가로 스크롤).
                "커스텀" 칩 모드에선 아래 그리드가 전용 뷰가 되므로 스트립은 숨긴다 */}
            {!usergenOnly && <UsergenAssetStrip />}

            {/* 히어로 — 모은 개수 / 전체 대비 % */}
            <Box
              sx={{
                borderRadius: "20px",
                p: "18px 20px",
                background: GROUP_GRADIENT,
                boxShadow: "0 10px 28px rgba(124,58,237,0.3)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: "rgba(255,255,255,0.85)",
                  letterSpacing: "0.3px",
                }}
              >
                {t("galaxy.album.hero")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "6px",
                  mt: "4px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 34,
                    fontWeight: 900,
                    color: "#fff",
                    lineHeight: 1,
                  }}
                >
                  {totalCollected}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: "rgba(255,255,255,0.9)",
                  }}
                >
                  / {totalAssets}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 900,
                    color: "#fff",
                    lineHeight: 1,
                  }}
                >
                  {collectedPercent}%
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: "12px",
                  height: 8,
                  borderRadius: "999px",
                  bgcolor: "rgba(255,255,255,0.25)",
                  overflow: "hidden",
                }}
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${collectedPercent}%` }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  style={{
                    height: "100%",
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.95)",
                  }}
                />
              </Box>
            </Box>

            {/* 등급 분포 (탭하여 필터) */}
            {gradeStats.rows.length > 0 && (
              <Box
                sx={{
                  opacity: favOnly ? 0.4 : 1,
                  transition: "opacity 0.15s ease",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    mb: "8px",
                  }}
                >
                  <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fgLow }}>
                    {t("galaxy.album.byGrade")}
                  </Typography>
                  {gradeSel.size > 0 ? (
                    <ButtonBase
                      onClick={() => {
                        setFavOnly(false);
                        setGradeSel(new Set());
                      }}
                      sx={{ borderRadius: "999px", px: "6px" }}
                    >
                      <Typography
                        sx={{ fontSize: 11, fontWeight: 800, color: c.primary }}
                      >
                        {t("home.filter.all")}
                      </Typography>
                    </ButtonBase>
                  ) : (
                    <Typography
                      sx={{ fontSize: 10, fontWeight: 700, color: c.fgLower }}
                    >
                      {t("galaxy.album.tapToFilter")}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    height: 10,
                    borderRadius: "999px",
                    overflow: "hidden",
                    bgcolor: c.bgInset,
                  }}
                >
                  {gradeStats.rows.map((r) => (
                    <Box
                      key={r.grade}
                      sx={{
                        width: `${(r.count / gradeStats.total) * 100}%`,
                        bgcolor: r.color,
                        opacity:
                          gradeSel.size === 0 || gradeSel.has(r.grade) ? 1 : 0.3,
                        transition: "opacity 0.15s ease",
                      }}
                    />
                  ))}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    mt: "10px",
                  }}
                >
                  {gradeStats.rows.map((r) => {
                    const on = gradeSel.has(r.grade);
                    return (
                      <ButtonBase
                        key={r.grade}
                        onClick={() => toggleGrade(r.grade)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          px: "10px",
                          height: 28,
                          borderRadius: "999px",
                          bgcolor: on ? c.primary : c.bgInset,
                          border: `1px solid ${on ? c.primary : c.border}`,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: "5px",
                            bgcolor: r.color,
                            fontSize: 11,
                            fontWeight: 900,
                            color: "#fff",
                            lineHeight: "18px",
                            textAlign: "center",
                          }}
                        >
                          {r.grade}
                        </Box>
                        <Typography
                          sx={{ ...TEMP_TEXT_BOLD_XS, color: on ? "#fff" : c.fg }}
                        >
                          {r.count}
                        </Typography>
                      </ButtonBase>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* 친밀도 분포 (탭하여 필터) + 에피소드 필터 칩 */}
            {(intimacyStats.length > 0 || episodeCount > 0) && (
              <Box
                sx={{
                  opacity: favOnly ? 0.4 : 1,
                  transition: "opacity 0.15s ease",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    mb: "8px",
                  }}
                >
                  <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: c.fgLow }}>
                    {t("galaxy.album.byIntimacy")}
                  </Typography>
                  {intimacySel.size > 0 ? (
                    <ButtonBase
                      onClick={() => {
                        setFavOnly(false);
                        setIntimacySel(new Set());
                      }}
                      sx={{ borderRadius: "999px", px: "6px" }}
                    >
                      <Typography
                        sx={{ fontSize: 11, fontWeight: 800, color: c.primary }}
                      >
                        {t("home.filter.all")}
                      </Typography>
                    </ButtonBase>
                  ) : (
                    <Typography
                      sx={{ fontSize: 10, fontWeight: 700, color: c.fgLower }}
                    >
                      {t("galaxy.album.tapToFilter")}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {intimacyStats.map((b) => {
                    const on = intimacySel.has(b.idx);
                    return (
                      <ButtonBase
                        key={b.idx}
                        onClick={() => toggleIntimacy(b.idx)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          px: "10px",
                          height: 28,
                          borderRadius: "999px",
                          bgcolor: on ? c.primary : c.bgInset,
                          border: `1px solid ${on ? c.primary : c.border}`,
                        }}
                      >
                        <Icon
                          name="icHeartFilled"
                          size={11}
                          color={on ? "#fff" : HEART_COLOR}
                        />
                        <Typography
                          sx={{
                            ...TEMP_TEXT_SEMIBOLD_XS,
                            color: on ? "#fff" : c.fgLow,
                          }}
                        >
                          {t(b.label)}
                        </Typography>
                        <Typography
                          sx={{ ...TEMP_TEXT_BOLD_XS, color: on ? "#fff" : c.fg }}
                        >
                          {b.count}
                        </Typography>
                      </ButtonBase>
                    );
                  })}
                  {/* 에피소드 필터 칩 — 탭하면 에피소드 컷만 */}
                  {episodeCount > 0 && (
                    <ButtonBase
                      onClick={() => {
                        setFavOnly(false);
                        setUsergenOnly(false);
                        setEpisodeOnly((v) => !v);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        px: "10px",
                        height: 28,
                        borderRadius: "999px",
                        bgcolor: episodeOnly ? EPISODE_CHIP_COLOR : c.bgInset,
                        border: `1px solid ${episodeOnly ? EPISODE_CHIP_COLOR : c.border}`,
                      }}
                    >
                      <Typography
                        sx={{
                          ...TEMP_TEXT_SEMIBOLD_XS,
                          color: episodeOnly ? "#fff" : EPISODE_CHIP_COLOR,
                        }}
                      >
                        {t("chat.filter.episode")}
                      </Typography>
                      <Typography
                        sx={{ ...TEMP_TEXT_BOLD_XS, color: episodeOnly ? "#fff" : c.fg }}
                      >
                        {episodeCount}
                      </Typography>
                    </ButtonBase>
                  )}
                </Box>
              </Box>
            )}

            {/* 필터 (즐겨찾기 / 수위) */}
            {totalCollected > 0 && (
              <Box sx={{ display: "flex", gap: "8px", pt: "2px" }}>
                <FilterChip
                  label={t("galaxy.album.soft")}
                  count={softCount}
                  active={filter.has("soft")}
                  onClick={() => toggleChip("soft")}
                  c={c}
                  dimmed={favOnly}
                />
                <FilterChip
                  label={t("galaxy.album.hard")}
                  count={hardCount}
                  active={filter.has("hard")}
                  onClick={() => toggleChip("hard")}
                  c={c}
                  dimmed={favOnly}
                />
                {/* 구분선 — '수위' 카테고리와 '즐겨찾기'는 다른 축임을 표시 */}
                <Box
                  sx={{
                    alignSelf: "center",
                    width: "1px",
                    height: 20,
                    bgcolor: c.border,
                    flexShrink: 0,
                  }}
                />
                <FilterChip
                  label={t("galaxy.album.favorite")}
                  count={favCount}
                  active={favOnly}
                  onClick={() => {
                    setUsergenOnly(false);
                    setFavOnly((v) => !v);
                  }}
                  c={c}
                  icon={
                    <StarIcon
                      size={13}
                      filled={favOnly}
                      color={favOnly ? "#fff" : FAVORITE_COLOR}
                    />
                  }
                />
                {/* 커스텀(직접생성)만 보기 — 별개 모드 (즐겨찾기와 동일 축) */}
                {usergenCount > 0 && (
                  <FilterChip
                    label={t("museAsset.chip_custom")}
                    count={usergenCount}
                    active={usergenOnly}
                    onClick={() => {
                      setFavOnly(false);
                      setUsergenOnly((v) => !v);
                    }}
                    c={c}
                    dimmed={favOnly}
                  />
                )}
              </Box>
            )}

            {/* 그리드 — "커스텀" 칩 모드면 직접생성 전용 그리드로 대체 */}
            {usergenOnly ? (
              <UsergenAssetStrip layout="grid" />
            ) : totalCollected === 0 ? (
              <EmptyState
                icon="icTrophy"
                title={t("galaxy.album.emptyTitle")}
                subtitle={t("galaxy.album.emptySubtitle")}
              />
            ) : visible.length === 0 ? (
              <Box sx={{ py: "48px", textAlign: "center" }}>
                <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_M, color: c.fgLow }}>
                  {t("galaxy.album.emptyFiltered")}
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "8px",
                  }}
                >
                  {visible.slice(0, shownCount).map((a, idx) => (
                    <AssetTile
                      key={a.id}
                      asset={a}
                      onClick={() => setViewerIndex(idx)}
                      c={c}
                    />
                  ))}
                </Box>
                {/* 바닥 센티넬 — 보이면 다음 묶음 로드 */}
                <Box ref={loadMoreRef} sx={{ height: "1px" }} />
                {shownCount < visible.length && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: "16px",
                    }}
                  >
                    <CircularProgress size={22} sx={{ color: c.primary }} />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Box>

      {/* 풀스크린 뷰어 — 열 때마다 새로 마운트(초기 인덱스로 시작) */}
      {viewerIndex !== null && (
        <AssetViewer
          items={visible}
          initialIndex={viewerIndex}
          language={language}
          onClose={() => setViewerIndex(null)}
          onToggleFavorite={toggleFavorite}
        />
      )}
    </Box>
  );
}

type ColorTokens = ReturnType<typeof useDarkColors>;

function FilterChip({
  label,
  count,
  active,
  onClick,
  c,
  icon,
  dimmed = false,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  c: ColorTokens;
  icon?: ReactNode;
  dimmed?: boolean;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        px: "14px",
        height: 34,
        borderRadius: "999px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        bgcolor: active ? c.primary : c.bgInset,
        border: `1px solid ${active ? c.primary : c.border}`,
        opacity: dimmed ? 0.4 : 1,
        transition: "opacity 0.15s ease",
      }}
    >
      {icon && (
        <Box sx={{ display: "flex", alignItems: "center", ml: "-2px" }}>
          {icon}
        </Box>
      )}
      <Typography sx={{ ...TEMP_TEXT_BOLD_XS, color: active ? "#fff" : c.fg }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          color: active ? "rgba(255,255,255,0.85)" : c.fgLower,
        }}
      >
        {count}
      </Typography>
    </ButtonBase>
  );
}

function AssetTile({
  asset,
  onClick,
  c,
}: {
  asset: CollectedAsset;
  onClick: () => void;
  c: ColorTokens;
}) {
  const { t } = useTranslator();
  const isVideo = asset.assetType === "VIDEO";
  const thumb = isVideo
    ? getAssetUrl(getVideoPosterPath(asset.assetPath), "small")
    : getAssetUrl(asset.assetPath, "small");
  const gradeColor = GACHA_GRADE_CONFIG[asset.grade]?.borderColor ?? "#888";
  const avatar = asset.profileImagePath
    ? getAssetUrl(asset.profileImagePath, "large")
    : "";

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "3 / 4",
        borderRadius: "12px",
        overflow: "hidden",
        bgcolor: c.bgInset,
        display: "block",
        ...(asset.isGacha
          ? { background: GRADE_FRAME[asset.grade] ?? "#555", p: "2.5px" }
          : { boxShadow: `inset 0 0 0 1px ${c.border}` }),
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: asset.isGacha ? "10px" : "12px",
          overflow: "hidden",
          bgcolor: c.bgInset,
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={thumb}
          alt=""
          draggable={false}
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/images/blur.png";
          }}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />

        {/* 등급 배지 (뽑기 아이템) */}
        {asset.isGacha && (
          <Box
            component="span"
            sx={{
              position: "absolute",
              top: 5,
              left: 5,
              zIndex: 2,
              width: 18,
              height: 18,
              borderRadius: "5px",
              bgcolor: gradeColor,
              fontSize: 11,
              fontWeight: 900,
              color: "#fff",
              lineHeight: "18px",
              textAlign: "center",
              boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
            }}
          >
            {asset.grade}
          </Box>
        )}

        {/* 에피소드 배지 — 등급(L/U/S) 배지와 같은 자리(에피소드 컷은 뽑기가 아니라 겹치지 않음) */}
        {asset.isEpisode && (
          <Box
            component="span"
            sx={{
              position: "absolute",
              top: 5,
              left: 5,
              zIndex: 2,
              px: "6px",
              height: 18,
              borderRadius: "5px",
              bgcolor: EPISODE_CHIP_COLOR,
              fontSize: 10,
              fontWeight: 900,
              color: "#fff",
              lineHeight: "18px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
            }}
          >
            {t("chat.filter.episode")}
          </Box>
        )}

        {/* 친밀도 배지 (뽑기 아닌 에셋) */}
        {!asset.isGacha && asset.intimacyLevel > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 5,
              left: 5,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              gap: "2px",
              px: "5px",
              height: 18,
              borderRadius: "999px",
              bgcolor: "rgba(0,0,0,0.55)",
            }}
          >
            <Icon name="icHeartFilled" size={9} color={HEART_COLOR} />
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1,
              }}
            >
              {asset.intimacyLevel * 10}°
            </Typography>
          </Box>
        )}

        {/* 영상/음성 표시 (우상단) */}
        {(isVideo || !!asset.voicePath) && (
          <Box
            sx={{
              position: "absolute",
              top: 5,
              right: 5,
              zIndex: 2,
              display: "flex",
              gap: "3px",
            }}
          >
            {isVideo && (
              <Box
                sx={{
                  bgcolor: "rgba(0,0,0,0.5)",
                  borderRadius: "50%",
                  width: 18,
                  height: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="icPlayFill" size={10} color="#fff" />
              </Box>
            )}
            {!!asset.voicePath && (
              <Box
                sx={{
                  bgcolor: "rgba(0,0,0,0.5)",
                  borderRadius: "50%",
                  width: 18,
                  height: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="icVolume" size={10} color="#fff" />
              </Box>
            )}
          </Box>
        )}

        {/* 캐릭터 아바타 (어떤 캐릭터인지 식별) */}
        <Box
          sx={{
            position: "absolute",
            bottom: 5,
            left: 5,
            zIndex: 2,
            width: 24,
            height: 24,
            borderRadius: "50%",
            overflow: "hidden",
            border: "1.5px solid rgba(255,255,255,0.9)",
            bgcolor: c.bgInset,
            boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
          }}
        >
          {avatar && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={avatar}
              alt={asset.characterName}
              draggable={false}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          )}
        </Box>

        {/* 즐겨찾기 표시 (우하단) */}
        {asset.isFavorite && (
          <Box
            sx={{
              position: "absolute",
              bottom: 5,
              right: 5,
              zIndex: 2,
              width: 22,
              height: 22,
              borderRadius: "50%",
              bgcolor: "rgba(0,0,0,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
            }}
          >
            <StarIcon size={12} filled color={FAVORITE_COLOR} />
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
}

function EmptyState({
  icon,
  title,
  subtitle,
  cta,
}: {
  icon: "icLock" | "icTrophy";
  title: string;
  subtitle: string;
  cta?: { label: string; onClick: () => void };
}) {
  const c = useDarkColors();
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        px: "40px",
        py: "60px",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: c.isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon name={icon} size={28} color={c.fgLower} />
      </Box>
      <Typography sx={{ ...TEMP_TEXT_BOLD_L, color: c.fg }}>{title}</Typography>
      <Typography
        sx={{ ...TEMP_TEXT_SEMIBOLD_M, color: c.fgLow, whiteSpace: "pre-line" }}
      >
        {subtitle}
      </Typography>
      {cta && (
        <ButtonBase
          onClick={cta.onClick}
          sx={{
            mt: "6px",
            px: "24px",
            py: "13px",
            borderRadius: "999px",
            background: GROUP_GRADIENT,
            boxShadow: "0 8px 22px rgba(124,58,237,0.4)",
          }}
        >
          <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: "#fff" }}>
            {cta.label}
          </Typography>
        </ButtonBase>
      )}
    </Box>
  );
}

// ============================================================
// 풀스크린 뷰어 — 가로 스와이프 + 핀치줌 + 음성. 캐릭터 프로필 정보 헤더.
// ============================================================

function AssetViewer({
  items,
  initialIndex,
  language,
  onClose,
  onToggleFavorite,
}: {
  items: CollectedAsset[];
  initialIndex: number;
  language: string;
  onClose: () => void;
  onToggleFavorite: (assetId: string, next: boolean) => void;
}) {
  const { t } = useTranslator();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [muted, setMuted] = useState(false);
  // 즐겨찾기 토스트 (잠깐 떴다 사라지는 알약)
  const [favToast, setFavToast] = useState<string | null>(null);
  const favToastTimer = useRef<number | null>(null);
  const showFavToast = useCallback((msg: string) => {
    setFavToast(msg);
    if (favToastTimer.current) window.clearTimeout(favToastTimer.current);
    favToastTimer.current = window.setTimeout(() => setFavToast(null), 1600);
  }, []);
  useEffect(
    () => () => {
      if (favToastTimer.current) window.clearTimeout(favToastTimer.current);
    },
    [],
  );
  // 핏 모드: 기본 풀스크린(cover=전체뷰), 토글 시 원본 비율(contain=비율뷰) — AlbumContent와 동일
  const [fitMode, setFitMode] = useState<"cover" | "contain">("cover");
  const isClosingRef = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const edgeRef = useRef<HTMLDivElement | null>(null);
  // 음성 재생용 단일 오디오 엘리먼트 (슬라이드 전환마다 재사용 → iOS 자동재생 정책 우회)
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playedSrcRef = useRef<string | null>(null);

  // 마운트 시 초기 인덱스로 즉시 점프.
  // scrollLeft 직접 대입은 CSS scroll-behavior:smooth 영향을 안 받아 애니메이션 없음.
  // (scrollTo({behavior:'auto'})는 CSS smooth를 따라가 0→목표로 "우다다다" 스크롤됨)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollLeft = initialIndex * el.clientWidth;
    });
    return () => cancelAnimationFrame(id);
  }, [initialIndex]);

  // 하드웨어 백/스와이프로 뷰어만 닫기 (스택 pop보다 우선)
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (typeof window === "undefined") return;

    isClosingRef.current = false;
    const GUARD = "__assetViewerGuard";
    history.pushState({ [GUARD]: true }, "");

    const handler = () => {
      if (isClosingRef.current) return;
      isClosingRef.current = true;
      history.pushState({ [GUARD]: true }, "");
      registerTopPriorityBackInterceptor(handler);
      onCloseRef.current();
    };
    registerTopPriorityBackInterceptor(handler);

    return () => {
      unregisterTopPriorityBackInterceptor(handler);
      if ((history.state as Record<string, unknown> | null)?.[GUARD] === true) {
        registerTopPriorityBackInterceptor(() => {});
        history.back();
      }
    };
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (w === 0) return;
    const idx = Math.round(el.scrollLeft / w);
    setActiveIndex((prev) => (idx !== prev ? idx : prev));
  }, []);

  // PC(마우스)에서는 터치 스와이프가 없으므로 화살표/키보드로 슬라이드 이동
  const goRelative = useCallback((dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goRelative(-1);
      else if (e.key === "ArrowRight") goRelative(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goRelative]);

  // 좌측 엣지 스와이프 → 뷰어만 닫기(→ 그리드). StackScreen의 엣지 스와이프(전체 화면 pop)를
  // 가로채서, 채팅으로 한 번에 빠지지 않고 챕터처럼 한 단계만 뒤로 가게 한다.
  useEffect(() => {
    const edge = edgeRef.current;
    const root = rootRef.current;
    if (!edge || !root) return;

    let startX = 0;
    let startY = 0;
    let dragging = false;
    let dx = 0;

    const onStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      dragging = true;
      dx = 0;
      root.style.transition = "none";
      e.stopPropagation(); // 부모 StackScreen 엣지 스와이프 차단
    };
    const onMove = (e: TouchEvent) => {
      if (!dragging) return;
      const cx = e.touches[0].clientX;
      const cy = e.touches[0].clientY;
      dx = cx - startX;
      const dy = cy - startY;
      if (Math.abs(dy) > Math.abs(dx)) return; // 세로 제스처는 무시
      if (dx < 0) dx = 0;
      e.preventDefault();
      root.style.transform = `translateX(${dx}px)`;
      root.style.opacity = String(Math.max(0.4, 1 - dx / window.innerWidth));
    };
    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      root.style.transition = "transform 0.25s ease, opacity 0.25s ease";
      if (dx > 90) {
        root.style.transform = "translateX(100%)";
        root.style.opacity = "0";
        window.setTimeout(() => onCloseRef.current(), 200);
      } else {
        root.style.transform = "translateX(0)";
        root.style.opacity = "1";
      }
    };

    edge.addEventListener("touchstart", onStart, { passive: false });
    edge.addEventListener("touchmove", onMove, { passive: false });
    edge.addEventListener("touchend", onEnd);
    return () => {
      edge.removeEventListener("touchstart", onStart);
      edge.removeEventListener("touchmove", onMove);
      edge.removeEventListener("touchend", onEnd);
    };
  }, []);

  const total = items.length;
  const active = items[Math.min(activeIndex, total - 1)];
  const activeVoiceUrl = active
    ? pickVoiceUrl(active.voicePath, language)
    : null;

  // 즐겨찾기 필터가 켜진 상태에서 마지막 항목을 해제하면 목록이 비므로 뷰어를 닫는다
  useEffect(() => {
    if (total === 0) onCloseRef.current();
  }, [total]);

  const handleToggleFavorite = useCallback(() => {
    if (!active) return;
    const next = !active.isFavorite;
    onToggleFavorite(active.id, next);
    showFavToast(next ? t("galaxy.viewer.favoriteAddedToast") : t("galaxy.viewer.favoriteRemovedToast"));
  }, [active, onToggleFavorite, showFavToast, t]);
  const activeIsVideo = active?.assetType === "VIDEO";
  const showSpeaker = !!activeVoiceUrl || activeIsVideo;
  const avatar = active?.profileImagePath
    ? getAssetUrl(active.profileImagePath, "large")
    : "";
  const gradeColor = active
    ? (GACHA_GRADE_CONFIG[active.grade]?.borderColor ?? "#888")
    : "#888";

  // 단일 오디오 엘리먼트 생성/해제 (최초 1회)
  useEffect(() => {
    const audio = new Audio();
    audio.loop = true;
    audio.preload = "auto";
    audioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = "";
      audioRef.current = null;
      playedSrcRef.current = null;
    };
  }, []);

  // 활성 슬라이드의 음성 자동재생 — 같은 엘리먼트를 재사용해 넘길 때마다 바로 소리.
  // (슬라이드마다 new Audio() 하면 첫 장 빼곤 자동재생 정책에 막힘)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // 비디오 자체 사운드는 슬라이드 <video>가 담당. 여기선 별도 음성(voiceUrl)만.
    if (!activeVoiceUrl || muted) {
      audio.pause();
      return;
    }
    if (playedSrcRef.current !== activeVoiceUrl) {
      audio.src = activeVoiceUrl;
      playedSrcRef.current = activeVoiceUrl;
      try {
        audio.currentTime = 0;
      } catch {}
    }
    const p = audio.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [activeVoiceUrl, muted]);

  return (
    <Box
      ref={rootRef}
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: "#000",
        zIndex: 20,
        overflow: "hidden",
        color: "#fff",
        touchAction: "pan-x",
      }}
    >
      {/* 좌측 엣지 스와이프 → 뷰어 닫기(그리드로). 상단바 아래부터 시작해 뒤로가기 버튼은 안 가림 */}
      <Box
        ref={edgeRef}
        sx={{ position: "absolute", left: 0, top: 70, bottom: 0, width: 44, zIndex: 5 }}
      />

      {/* 상단바 — 뒤로 + 캐릭터 프로필 + 등급/친밀도 + 인덱스 */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 3,
          pt: "env(safe-area-inset-top)",
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            height: 56,
            px: "12px",
          }}
        >
          <ButtonBase
            onClick={onClose}
            aria-label={t("discovery.close")}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon name="icArrowleft" size={24} color="#fff" />
          </ButtonBase>

          {active && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: 0,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  overflow: "hidden",
                  flexShrink: 0,
                  border: "1px solid rgba(255,255,255,0.6)",
                  bgcolor: "rgba(255,255,255,0.1)",
                }}
              >
                {avatar && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={avatar}
                    alt={active.characterName}
                    draggable={false}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 800,
                    color: "#fff",
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {active.characterName}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    mt: "2px",
                  }}
                >
                  {active.isGacha && (
                    <Box
                      component="span"
                      sx={{
                        width: 15,
                        height: 15,
                        borderRadius: "4px",
                        bgcolor: gradeColor,
                        fontSize: 9,
                        fontWeight: 900,
                        color: "#fff",
                        lineHeight: "15px",
                        textAlign: "center",
                      }}
                    >
                      {active.grade}
                    </Box>
                  )}
                  {active.intimacyLevel > 0 && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "2px" }}
                    >
                      <Icon name="icHeartFilled" size={9} color={HEART_COLOR} />
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 800,
                          color: "rgba(255,255,255,0.9)",
                          lineHeight: 1,
                        }}
                      >
                        {active.intimacyLevel * 10}°C
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 800,
              color: "rgba(255,255,255,0.85)",
              flexShrink: 0,
            }}
          >
            {Math.min(activeIndex + 1, total)} / {total}
          </Typography>
        </Box>
      </Box>

      {/* 슬라이드 캐러셀 */}
      <Box
        ref={scrollerRef}
        onScroll={handleScroll}
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "row",
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          touchAction: "pan-x",
        }}
      >
        {items.map((item, idx) => {
          // 윈도잉 — 활성 ±2만 실제 콘텐츠(이미지/영상 + 제스처 리스너) 마운트.
          // 멀리 있는 슬라이드는 같은 크기의 빈 자리로 둬서 스크롤 위치·스냅·스와이프
          // 계산은 그대로 유지하되, 수백 장이어도 DOM/리스너 비용이 일정하게 유지된다.
          if (Math.abs(idx - activeIndex) > 2) {
            return (
              <Box
                key={item.id}
                sx={{
                  flex: "0 0 100%",
                  width: "100%",
                  height: "100%",
                  scrollSnapAlign: "center",
                  scrollSnapStop: "always",
                  bgcolor: "#000",
                }}
              />
            );
          }
          return (
            <AssetSlide
              key={item.id}
              item={item}
              isActive={idx === activeIndex}
              eager={Math.abs(idx - activeIndex) <= 1}
              muted={muted}
              voiceUrl={pickVoiceUrl(item.voicePath, language)}
              fitMode={fitMode}
            />
          );
        })}
      </Box>

      {/* 핏 모드 토글 (전체뷰 cover ↔ 비율뷰 contain) — 항상 스피커 자리 위(72px) */}
      <Box
        onClick={() => setFitMode((m) => (m === "cover" ? "contain" : "cover"))}
        sx={{
          position: "absolute",
          right: 16,
          bottom: "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 72px)",
          width: 44,
          height: 44,
          borderRadius: "50%",
          bgcolor: "rgba(0,0,0,0.55)",
          border: "1px solid rgba(255,255,255,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
          zIndex: 3,
        }}
        aria-label={fitMode === "cover" ? t("galaxy.viewer.fitContain") : t("galaxy.viewer.fitCover")}
      >
        {fitMode === "cover" ? (
          <FitContainIcon size={18} />
        ) : (
          <FitCoverIcon size={18} />
        )}
      </Box>

      {/* 스피커 슬롯 — 소리 유무와 무관하게 자리를 항상 차지(핏 버튼이 안 흔들리게). 소리 없을 땐 투명 */}
      <Box
        onClick={showSpeaker ? () => setMuted((m) => !m) : undefined}
        sx={{
          position: "absolute",
          right: 16,
          bottom: "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 16px)",
          width: 44,
          height: 44,
          borderRadius: "50%",
          bgcolor: showSpeaker ? "rgba(0,0,0,0.55)" : "transparent",
          border: showSpeaker
            ? "1px solid rgba(255,255,255,0.18)"
            : "1px solid transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: showSpeaker ? "pointer" : "default",
          pointerEvents: showSpeaker ? "auto" : "none",
          WebkitTapHighlightColor: "transparent",
          zIndex: 3,
        }}
        aria-label={
          showSpeaker ? (muted ? t("galaxy.viewer.soundOn") : t("galaxy.viewer.soundOff")) : undefined
        }
        aria-hidden={!showSpeaker}
      >
        {showSpeaker &&
          (muted ? <SpeakerOffIcon size={18} /> : <SpeakerIcon size={18} />)}
      </Box>

      {/* 즐겨찾기 버튼 — 스피커 버튼 왼쪽 (우하단). 에피소드 컷(가상 id)은 즐겨찾기 미지원 */}
      {active && !active.id.startsWith("episode:") && (
        <Box
          onClick={handleToggleFavorite}
          sx={{
            position: "absolute",
            right: 72,
            bottom: "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 16px)",
            width: 44,
            height: 44,
            borderRadius: "50%",
            bgcolor: "rgba(0,0,0,0.55)",
            border: "1px solid rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
            zIndex: 3,
          }}
          aria-label={active.isFavorite ? t("galaxy.viewer.favoriteRemove") : t("galaxy.viewer.favoriteAdd")}
          aria-pressed={active.isFavorite}
        >
          <motion.div
            key={active.isFavorite ? "on" : "off"}
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 520, damping: 16 }}
            style={{ display: "flex" }}
          >
            <StarIcon
              size={21}
              filled={active.isFavorite}
              color={active.isFavorite ? FAVORITE_COLOR : "#fff"}
            />
          </motion.div>
        </Box>
      )}

      {/* 즐겨찾기 토스트 — 잠깐 떴다 사라지는 알약 */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 80px)",
          display: "flex",
          justifyContent: "center",
          zIndex: 6,
          pointerEvents: "none",
          px: "20px",
        }}
      >
        <AnimatePresence>
          {favToast && (
            <Box
              component={motion.div}
              key="fav-toast"
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                px: "16px",
                height: 40,
                borderRadius: "999px",
                bgcolor: "rgba(0,0,0,0.82)",
                border: "1px solid rgba(255,255,255,0.14)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
                whiteSpace: "nowrap",
              }}
            >
              <StarIcon size={15} filled color={FAVORITE_COLOR} />
              <Typography
                sx={{ fontSize: 13, fontWeight: 800, color: "#fff", lineHeight: 1 }}
              >
                {favToast}
              </Typography>
            </Box>
          )}
        </AnimatePresence>
      </Box>

      {/* PC 좌우 네비 (터치 기기에선 숨김) */}
      {total > 1 && (
        <>
          <ButtonBase
            onClick={() => goRelative(-1)}
            aria-label={t("chat.onboarding.prev")}
            sx={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              width: 44,
              height: 44,
              borderRadius: "50%",
              bgcolor: "rgba(0,0,0,0.45)",
              border: "1px solid rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 4,
              opacity: activeIndex <= 0 ? 0.3 : 1,
              pointerEvents: activeIndex <= 0 ? "none" : "auto",
              "@media (pointer: coarse)": { display: "none" },
            }}
          >
            <Icon name="icChevronLeft" size={22} color="#fff" />
          </ButtonBase>
          <ButtonBase
            onClick={() => goRelative(1)}
            aria-label={t("chat.onboarding.next")}
            sx={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              width: 44,
              height: 44,
              borderRadius: "50%",
              bgcolor: "rgba(0,0,0,0.45)",
              border: "1px solid rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 4,
              opacity: activeIndex >= total - 1 ? 0.3 : 1,
              pointerEvents: activeIndex >= total - 1 ? "none" : "auto",
              "@media (pointer: coarse)": { display: "none" },
            }}
          >
            <Icon name="icChevronRight" size={22} color="#fff" />
          </ButtonBase>
        </>
      )}
    </Box>
  );
}

type SlideGesture =
  | {
      type: "pinch";
      startDist: number;
      startScale: number;
      px: number;
      py: number;
      cx: number;
      cy: number;
    }
  | {
      type: "pan";
      startX: number;
      startY: number;
      startTx: number;
      startTy: number;
    };

function AssetSlide({
  item,
  isActive,
  eager,
  muted,
  voiceUrl,
  fitMode,
}: {
  item: CollectedAsset;
  isActive: boolean;
  eager: boolean;
  muted: boolean;
  voiceUrl: string | null;
  fitMode: "cover" | "contain";
}) {
  const isVideo = item.assetType === "VIDEO";
  const hasVoice = !!voiceUrl;
  const src = isVideo
    ? getAssetUrl(item.assetPath, "original")
    : getAssetUrl(item.assetPath, "large");
  // 영상 포스터는 _poster_small 까지만 생성됨 (large 변형 없음)
  const poster = isVideo
    ? getAssetUrl(getVideoPosterPath(item.assetPath), "small")
    : undefined;
  const videoMuted = hasVoice ? true : muted;

  const slideRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const transformRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const gestureRef = useRef<SlideGesture | null>(null);

  // 활성 슬라이드만 비디오 재생
  useEffect(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;
    if (isActive) {
      try {
        v.currentTime = 0;
      } catch {}
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      v.pause();
    }
  }, [isVideo, isActive]);

  // 음성(voiceUrl) 재생은 뷰어 레벨의 단일 오디오 엘리먼트가 담당 (자동재생 정책 우회).

  // 핀치/팬 — native non-passive 리스너 (AlbumContent와 동일 로직)
  useEffect(() => {
    const el = slideRef.current;
    if (!el) return;

    const applyTransform = () => {
      const cnt = contentRef.current;
      if (!cnt) return;
      const { scale, tx, ty } = transformRef.current;
      cnt.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
    };
    const setTouchAction = () => {
      el.style.touchAction =
        transformRef.current.scale > 1.001 ? "none" : "pan-x";
    };
    const clampPan = () => {
      const { scale } = transformRef.current;
      const w = el.clientWidth;
      const h = el.clientHeight;
      const maxX = ((scale - 1) * w) / 2;
      const maxY = ((scale - 1) * h) / 2;
      transformRef.current.tx = Math.max(
        -maxX,
        Math.min(maxX, transformRef.current.tx),
      );
      transformRef.current.ty = Math.max(
        -maxY,
        Math.min(maxY, transformRef.current.ty),
      );
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const rect = el.getBoundingClientRect();
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(
          t2.clientX - t1.clientX,
          t2.clientY - t1.clientY,
        );
        const midX = (t1.clientX + t2.clientX) / 2 - rect.left;
        const midY = (t1.clientY + t2.clientY) / 2 - rect.top;
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        const { scale, tx, ty } = transformRef.current;
        const px = (midX - cx - tx) / scale + cx;
        const py = (midY - cy - ty) / scale + cy;
        gestureRef.current = {
          type: "pinch",
          startDist: dist,
          startScale: scale,
          px,
          py,
          cx,
          cy,
        };
        e.preventDefault();
      } else if (e.touches.length === 1 && transformRef.current.scale > 1.001) {
        const t = e.touches[0];
        gestureRef.current = {
          type: "pan",
          startX: t.clientX,
          startY: t.clientY,
          startTx: transformRef.current.tx,
          startTy: transformRef.current.ty,
        };
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      const g = gestureRef.current;
      if (!g) return;
      if (g.type === "pinch" && e.touches.length === 2) {
        const rect = el.getBoundingClientRect();
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(
          t2.clientX - t1.clientX,
          t2.clientY - t1.clientY,
        );
        const midX = (t1.clientX + t2.clientX) / 2 - rect.left;
        const midY = (t1.clientY + t2.clientY) / 2 - rect.top;
        const newScale = Math.max(
          1,
          Math.min(4, g.startScale * (dist / g.startDist)),
        );
        const tx = midX - newScale * (g.px - g.cx) - g.cx;
        const ty = midY - newScale * (g.py - g.cy) - g.cy;
        transformRef.current.scale = newScale;
        transformRef.current.tx = tx;
        transformRef.current.ty = ty;
        clampPan();
        applyTransform();
        e.preventDefault();
      } else if (g.type === "pan" && e.touches.length === 1) {
        const t = e.touches[0];
        transformRef.current.tx = g.startTx + (t.clientX - g.startX);
        transformRef.current.ty = g.startTy + (t.clientY - g.startY);
        clampPan();
        applyTransform();
        e.preventDefault();
      }
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        gestureRef.current = null;
      } else if (
        e.touches.length === 1 &&
        gestureRef.current?.type === "pinch"
      ) {
        const t = e.touches[0];
        if (transformRef.current.scale > 1.001) {
          gestureRef.current = {
            type: "pan",
            startX: t.clientX,
            startY: t.clientY,
            startTx: transformRef.current.tx,
            startTy: transformRef.current.ty,
          };
        } else {
          gestureRef.current = null;
        }
      }
      if (transformRef.current.scale < 1.05) {
        transformRef.current = { scale: 1, tx: 0, ty: 0 };
        applyTransform();
      }
      setTouchAction();
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    el.addEventListener("touchcancel", onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  // 슬라이드를 벗어나거나 핏 모드가 바뀌면 줌 리셋 (AlbumContent와 동일)
  useEffect(() => {
    transformRef.current = { scale: 1, tx: 0, ty: 0 };
    gestureRef.current = null;
    const cnt = contentRef.current;
    if (cnt) cnt.style.transform = "translate3d(0,0,0) scale(1)";
    const s = slideRef.current;
    if (s) s.style.touchAction = "pan-x";
  }, [isActive, fitMode]);

  return (
    <Box
      ref={slideRef}
      sx={{
        flex: "0 0 100%",
        width: "100%",
        height: "100%",
        scrollSnapAlign: "center",
        scrollSnapStop: "always",
        position: "relative",
        bgcolor: "#000",
        overflow: "hidden",
        touchAction: "pan-x",
      }}
    >
      <div
        ref={contentRef}
        style={{
          position: "absolute",
          inset: 0,
          transformOrigin: "center center",
          willChange: "transform",
        }}
      >
        {isVideo ? (
          <video
            ref={videoRef}
            src={src}
            muted={videoMuted}
            loop
            playsInline
            preload={isActive ? "auto" : "metadata"}
            poster={poster}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: fitMode,
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={src}
            alt=""
            draggable={false}
            loading={eager ? "eager" : "lazy"}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: fitMode,
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    </Box>
  );
}

function StarIcon({
  size = 18,
  filled = false,
  color = "#fff",
}: {
  size?: number;
  filled?: boolean;
  color?: string;
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
        fill={filled ? color : "none"}
        stroke={color}
        strokeWidth={filled ? 1.2 : 1.8}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SpeakerIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fff" />
      <path
        d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpeakerOffIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M11 5L6 9H2v6h4l5 4V5z" fill="#fff" />
      <line
        x1="23"
        y1="9"
        x2="17"
        y2="15"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="17"
        y1="9"
        x2="23"
        y2="15"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// 현재 cover(전체뷰) → 누르면 contain(비율뷰)로 간다는 의미의 "맞춤/축소" 아이콘
function FitContainIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 4 14 4 14 10" />
      <polyline points="4 20 10 20 10 14" />
      <line x1="20" y1="4" x2="14" y2="10" />
      <line x1="4" y1="20" x2="10" y2="14" />
    </svg>
  );
}

// 현재 contain(비율뷰) → 누르면 cover(전체뷰)로 간다는 의미의 "확대" 아이콘
function FitCoverIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

// ============================================================
// 직접생성 스냅샷·배경 스트립 — 나의 세계/앨범에서 커스텀 에셋을 다시 본다.
// 탭하면 풀스크린으로 확대 + 스냅샷은 남긴 순간/뮤즈의 감정까지 표시.
// ============================================================

function UsergenAssetStrip({ layout = "strip" }: { layout?: "strip" | "grid" }) {
  const c = useDarkColors();
  const { t } = useTranslator();
  const [viewing, setViewing] = useState<MuseAssetItem | null>(null);

  const { data } = useQuery({
    queryKey: ["muse-asset", "list", "all"],
    queryFn: () => getMuseAssetList(),
    staleTime: 30_000,
  });
  const items = (data?.items ?? []).filter((it) => it.status === "COMPLETED" && it.assetPath);
  if (items.length === 0) return null;
  const isGrid = layout === "grid";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", px: "2px" }}>
        <Icon name="icCamera" size={16} color={c.isDark ? "#a78bfa" : c.primary} />
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: c.fg }}>
          {t("museAsset.gallery.title")}
        </Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: c.fgLower }}>{items.length}</Typography>
      </Box>
      <Box
        sx={
          isGrid
            ? { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }
            : {
                display: "flex",
                gap: "8px",
                overflowX: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                mx: "-16px",
                px: "16px",
              }
        }
      >
        {items.map((item) => {
          const src = getAssetUrl(
            item.mediaType === "VIDEO" ? (item.posterPath ?? item.assetPath!) : item.assetPath!,
            item.mediaType === "VIDEO" ? undefined : "small",
          );
          return (
            <ButtonBase
              key={item.id}
              onClick={() => setViewing(item)}
              sx={{
                ...(isGrid ? { width: "100%" } : { width: 108, flexShrink: 0 }),
                aspectRatio: "3/4",
                borderRadius: "12px",
                overflow: "hidden",
                position: "relative",
                bgcolor: c.bgInset,
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <Box component="img" src={src} alt="" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
              <Box sx={{ position: "absolute", top: 6, left: 6, background: "linear-gradient(100deg, #7c3aed 0%, #db2777 100%)", borderRadius: "9999px", px: "6px", py: "2px" }}>
                <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: "#fff" }}>
                  {t("chat.bg_sheet.custom_badge")}
                </Typography>
              </Box>
              {item.mediaType === "VIDEO" && (
                <Box sx={{ position: "absolute", bottom: 6, right: 6, width: 22, height: 22, borderRadius: "50%", bgcolor: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="icPlayFill" size={11} color="#fff" />
                </Box>
              )}
            </ButtonBase>
          );
        })}
      </Box>

      {/* 풀스크린 뷰어 — 미디어 + 스냅샷 상세 */}
      <AnimatePresence>
        {viewing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: "fixed", inset: 0, zIndex: 1400, background: "rgba(0,0,0,0.92)", display: "flex", flexDirection: "column" }}
            onClick={() => setViewing(null)}
          >
            <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "flex-end", p: "14px", pt: "calc(env(safe-area-inset-top, 0px) + 14px)" }}>
              <ButtonBase onClick={() => setViewing(null)} sx={{ width: 38, height: 38, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.12)" }}>
                <Icon name="icClose" size={18} color="#fff" />
              </ButtonBase>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", px: "16px" }} onClick={(e) => e.stopPropagation()}>
              {viewing.mediaType === "VIDEO" ? (
                <Box
                  component="video"
                  src={getAssetUrl(viewing.assetPath!)}
                  poster={viewing.posterPath ? getAssetUrl(viewing.posterPath) : undefined}
                  controls
                  autoPlay
                  playsInline
                  // 전역 video pointer-events:none 해제 — 재생/시크 컨트롤이 실터치로 동작하게
                  sx={{ maxWidth: "100%", maxHeight: "100%", borderRadius: "14px", pointerEvents: "auto" }}
                />
              ) : (
                <Box component="img" src={getAssetUrl(viewing.assetPath!)} alt="" sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "14px" }} />
              )}
            </Box>
            {(viewing.sceneSummary || viewing.emotion) && (
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{ flexShrink: 0, px: "20px", pb: "calc(env(safe-area-inset-bottom, 0px) + 22px)", pt: "14px", display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {viewing.sceneSummary && (
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#fff", lineHeight: 1.55 }}>
                    {viewing.sceneSummary}
                  </Typography>
                )}
                {viewing.emotion && (
                  <Typography sx={{ fontSize: 13, fontWeight: 400, color: "rgba(255,224,150,0.9)", lineHeight: 1.55 }}>
                    {viewing.emotion}
                  </Typography>
                )}
              </Box>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
